import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AuthService } from '../../../core/authentication/auth.service';
import {
  passwordGuidance as passwordPolicyGuidance,
  passwordsMatch,
  strongPasswordPattern,
} from '../../../core/authentication/password-policy';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
  ],
  selector: 'app-change-password-dialog',
  styleUrl: './change-password-dialog.scss',
  templateUrl: './change-password-dialog.html',
})
export class ChangePasswordDialog {
  private readonly authService = inject(AuthService);

  protected readonly loading = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly successMessage = signal<string | null>(null);
  protected readonly passwordGuidance = passwordPolicyGuidance;

  protected readonly form = new FormGroup({
    currentPassword: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    newPassword: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(strongPasswordPattern)],
    }),
    confirmation: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  protected async submit(): Promise<void> {
    if (this.form.invalid || this.loading()) {
      this.form.markAllAsTouched();
      return;
    }

    const { currentPassword, newPassword, confirmation } =
      this.form.getRawValue();
    if (!passwordsMatch(newPassword, confirmation)) {
      this.errorMessage.set('Las contraseñas no coinciden.');
      return;
    }

    this.loading.set(true);
    this.errorMessage.set(null);
    this.successMessage.set(null);
    this.form.disable();
    try {
      await this.authService.changePassword(currentPassword, newPassword);
      this.form.reset();
      this.successMessage.set('La contraseña se actualizó correctamente.');
    } catch (error) {
      this.errorMessage.set(toFriendlyChangePasswordError(error));
    } finally {
      this.form.enable();
      this.loading.set(false);
    }
  }
}

function toFriendlyChangePasswordError(error: unknown): string {
  const name =
    typeof error === 'object' && error !== null && 'name' in error
      ? String(error.name)
      : '';

  switch (name) {
    case 'NotAuthorizedException':
      return 'La contraseña actual no es correcta.';
    case 'InvalidPasswordException':
    case 'InvalidParameterException':
      return 'La nueva contraseña no cumple la política requerida.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'Se realizaron demasiados intentos. Espera un momento e inténtalo nuevamente.';
    default:
      return 'No fue posible cambiar la contraseña. Inténtalo nuevamente.';
  }
}
